class FlashSaleEmailCollector extends HTMLElement {
  constructor() {
    super();

    // Initialize component
    this.render();
    this.init();
    this.checkTimerStatus();
    this.hide();
  }

  render() {
    const heading = this.getAttribute('heading') || 'Previous flash sale has ended';
    const subtitle =
      this.getAttribute('subtitle') ||
      "Don't miss the next one. Leave your email address and we will send you a reminder when the next sale is on.";
    const buttonText = this.getAttribute('button-text') || 'Notify Me';
    const successMessage =
      this.getAttribute('success-message') ||
      "Thank you! We'll notify you when the next flash sale begins.";

    const backgroundColor = this.getAttribute('background-color') || '#f5f5f5';
    const textColor = this.getAttribute('text-color') || '#333';
    const buttonBgColor = this.getAttribute('button-bg-color') || '#000';
    const buttonTextColor = this.getAttribute('button-text-color') || '#fff';

    this.innerHTML = this.getInlineTemplate(
      heading,
      subtitle,
      buttonText,
      successMessage,
      backgroundColor,
      textColor,
      buttonBgColor,
      buttonTextColor
    );
  }

  getInlineTemplate(
    heading,
    subtitle,
    buttonText,
    successMessage,
    backgroundColor,
    textColor,
    buttonBgColor,
    buttonTextColor
  ) {
    const uniqueId = this.generateId();
    const emailId = `flash-sale-email-${uniqueId}`;
    const errorId = `flash-sale-error-${uniqueId}`;
    const successId = `flash-sale-success-${uniqueId}`;
    const submitDescId = `flash-sale-submit-desc-${uniqueId}`;
    const formId = `flash-sale-form-${uniqueId}`;

    return `
      <div class='flash-sale-banner flash-sale-email-collector'>
        <div class='flash-sale-content'>
          <h2 class='flash-sale-title'>
            <span class='flash'>FLASH SALE<span class="timer-ended">Has Ended</span></span>
          </h2>
          <p class='flash-sale-subtitle'>Don’t miss the next one. Enter your email address to get notified.</p>

          <div class='flash-sale-timer'>
              <form id="${formId}" class="flash-sale-form" novalidate role="form" aria-labelledby="${formId}">
                  <div class="flash-sale-form-group">
                    <input 
                      type="email" 
                      id="${emailId}" 
                      name="email" 
                      class="flash-sale-input" 
                      placeholder="Enter your email address" 
                      required 
                      autocomplete="email" 
                      aria-required="true" 
                      aria-describedby="${errorId} ${submitDescId}"
                      aria-invalid="false"
                      spellcheck="false"
                      aria-label="Email address for flash sale notifications"
                    />
                    <button 
                      type="submit" 
                      class="flash-sale-submit" 
                      data-submit 
                      style="background-color: ${buttonBgColor}; color: ${buttonTextColor};"
                      aria-describedby="${submitDescId}"
                      aria-label="${buttonText} - Submit your email to be notified about future flash sales"
                    >
                      <span class="button-text">${buttonText}</span>
                    </button>
                    <span id="${submitDescId}" class="visually-hidden">Submit your email to be notified about future flash sales</span>
                  </div>
                  <div id="${errorId}" class="flash-sale-error hidden" data-error role="alert" aria-live="assertive" aria-atomic="true"></div>
                </form>
              
              <div id="${successId}" class="flash-sale-success hidden" data-success role="status" aria-live="polite" aria-atomic="true">
                <p>${successMessage}</p>
              </div>
          </div>
        </div>
      </div>
    `;
  }

  generateId() {
    return Math.random().toString(36).substr(2, 9);
  }

  init() {
    this.setupFormHandling();
    this.setupAccessibility();
  }

  setupFormHandling() {
    const form = this.querySelector('.flash-sale-form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  setupAccessibility() {
    // Add keyboard navigation support
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hide();
      }
    });

    // Set initial ARIA attributes
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const submitButton = form.querySelector('[data-submit]');
    const emailInput = form.querySelector('input[type="email"]');
    const errorElement = this.querySelector('.flash-sale-error');
    const successElement = this.querySelector('.flash-sale-success');
    
    // Reset previous states
    this.hideError();
    this.hideSuccess();
    
    // Validate email
    const email = emailInput.value.trim();
    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address.');
      emailInput.focus();
      return;
    }
    
    // Show loading state
    this.setLoadingState(submitButton, true);
    
    try {
      // Simulate API call (replace with actual implementation)
      await this.submitEmail(email);
      
      // Show success
      this.showSuccess();
      form.reset();
      
      // Hide after delay
      setTimeout(() => {
        this.hide();
      }, 3000);
      
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.setLoadingState(submitButton, false);
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async submitEmail(email) {
    // Replace this with your actual email submission logic
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate success
        resolve();
      }, 1000);
    });
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('aria-label', 'Submitting...');
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      button.setAttribute('aria-label', button.querySelector('.button-text').textContent);
    }
  }

  showError(message) {
    const errorElement = this.querySelector('.flash-sale-error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
      errorElement.setAttribute('aria-live', 'assertive');
    }
  }

  hideError() {
    const errorElement = this.querySelector('.flash-sale-error');
    if (errorElement) {
      errorElement.classList.add('hidden');
      errorElement.textContent = '';
    }
  }

  showSuccess() {
    const successElement = this.querySelector('.flash-sale-success');
    if (successElement) {
      successElement.classList.remove('hidden');
      successElement.setAttribute('aria-live', 'polite');
    }
  }

  hideSuccess() {
    const successElement = this.querySelector('.flash-sale-success');
    if (successElement) {
      successElement.classList.add('hidden');
    }
  }

  checkTimerStatus() {
    // Check if countdown timer has ended
    const timers = document.querySelectorAll('countdown-timer');
    timers.forEach(timer => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-label') {
            const ariaLabel = timer.getAttribute('aria-label');
            if (ariaLabel && ariaLabel.includes('ended')) {
              this.show();
            }
          }
        });
      });
      
      observer.observe(timer, {
        attributes: true,
        attributeFilter: ['aria-label']
      });
    });
  }

  show() {
    this.style.display = 'block';
    this.setAttribute('aria-hidden', 'false');
    
    // Focus management
    const emailInput = this.querySelector('.flash-sale-input');
    if (emailInput) {
      emailInput.focus();
    }
  }

  hide() {
    this.style.display = 'none';
    this.setAttribute('aria-hidden', 'true');
    
    // Reset form state
    this.hideError();
    this.hideSuccess();
    const form = this.querySelector('.flash-sale-form');
    if (form) {
      form.reset();
    }
  }

  // Static method to show all collectors
  static showAllCollectors() {
    const collectors = document.querySelectorAll('flash-sale-email-collector');
    collectors.forEach(collector => {
      collector.show();
    });
  }
}

// Global instance for external access
window.FlashSaleEmailCollector = FlashSaleEmailCollector;

// Initialize all collectors on DOM load
document.addEventListener('DOMContentLoaded', () => {
  const collectors = document.querySelectorAll('flash-sale-email-collector');
  collectors.forEach(collector => {
    // Initialize if not already done
    if (!collector.initialized) {
      collector.initialized = true;
    }
  });
});

// Register the custom element
customElements.define('flash-sale-email-collector', FlashSaleEmailCollector);
